import { useMemo, useState } from 'react';

import { EventForUser } from 'legacy/events/types';
import { useGetTierFromShareTokenCommand } from '../../../../hooks';
import { EventPricingTier } from 'legacy/common/types/models';
import { ErrorResponse } from 'legacy/common/utils/error';
import isTierAvailable from '../../../../utils';

type UnlockPrivateTierFn = (
  code: string,
  options?: {
    onSuccess?: (tier: { tierId: string }) => void;
    onError?: (error: ErrorResponse) => void;
  }
) => void;

/**
 * Sorts tiers based on whether they are private or not.
 */
function sortTiers(a: EventPricingTier, b: EventPricingTier) {
  if (a.private === b.private) {
    return 0;
  }

  if (a.private) {
    return -1;
  }

  return 1;
}

/**
 * Retrieves ticket tiers from an event.
 */
const useMultiTiers = (event: EventForUser) => {
  const [privateTierCodes, setPrivateTierCodes] = useState({});
  const { mutate, isLoading, error } = useGetTierFromShareTokenCommand();

  // Filter out private tiers but includes private tiers that have been unlocked AND sort the tickets by private first.
  const tiers = useMemo(
    () =>
      event?.pricing?.tiers
        .filter((tier) => !tier.private || !!privateTierCodes[tier._id])
        .sort(sortTiers),
    [event?.pricing?.tiers, privateTierCodes]
  );

  const findTier = (tierId: string) =>
    event?.pricing?.tiers.find((tier) => tier._id === tierId);

  /**
   * Sets the private tier code for a given tier ID.
   */
  const setPrivateTierCode = (tierId: string, code: string) => {
    setPrivateTierCodes((privateTierCodes) => ({
      ...privateTierCodes,
      [tierId]: code
    }));
  };

  /**
   * Function to unlock a private tier with a given code.
   */
  const unlockPrivateTier: UnlockPrivateTierFn = (code, options) => {
    mutate(
      {
        eventId: event._id,
        shareToken: code
      },
      {
        onSuccess: (tier) => {
          setPrivateTierCode(tier.tierId, code);
          options?.onSuccess?.(tier);
        },
        onError: (err) => {
          options?.onError?.(err);
        }
      }
    );
  };

  const isCodeAlreadyActive = (code: string) =>
    Object.values(privateTierCodes).includes(code);

  const isTierRequiringApproval = (code: string) => {
    const tier = findTier(code);
    return tier?.requiresApproval && tier?.requestStatus !== 'approved';
  };

  const isTierDisabled = (code: string) => {
    const tier = findTier(code);
    return Boolean(
      tier &&
        (tier.isDisabled ||
          !isTierAvailable(tier) ||
          tier.requestStatus === 'pending' ||
          tier.requestStatus === 'rejected' ||
          tier.status === 'max')
    );
  };

  return {
    data: tiers,
    getTierFromShareToken: mutate,
    getTierFromShareTokenError: error,
    isLoadingGetTierFromShareToken: isLoading,
    unlockPrivateTier,
    setPrivateTierCode,
    privateTierCodes,
    isCodeAlreadyActive,
    isTierRequiringApproval,
    isTierDisabled
  };
};

export default useMultiTiers;
